import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import ExternalPayImage from '../../resource/externalPayImage.png';
import AfterPayImage from '../../resource/figma-images/afterpay-payment-image.svg';
import ZipPayImage from '../../resource/figma-images/zippay-payment-image.svg';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import AliPayDanaImage from '../../resource/dana.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import { FiservIpgCardPayment } from './FiservIpgCardPayment';
import { Alert, Stack, styled } from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getCreditCardPaymentImage,
  getDefaultPaymentMethod,
  getDisplaySurchargeRate,
  getPageHeroLogo,
  getPageTitle,
  getReferencePlaceholder,
  getThemeColor,
  shouldHideAlipayPlus,
  shouldShowDevEnvPaymentOptions,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import { hexToRGBA } from "../../utils/hexToRGBA";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import { PAYMENT_METHOD_FISERV_IPG } from "../constants";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;

const PaymentOptionList = styled('div')`
  display: flex;
  justify-content: space-between;
  // Should not use overflow: scroll because some of the browsers will have a disabled scroll bar forever.
`;

const PaymentOption = styled('div', {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'borderColor'
})(({ selected, borderColor }) => ({
  border: selected ? `2px solid ${borderColor}` : 'none',
  padding: '10px 20px',
  flexGrow: 1,
  flexBasis: '10%',
  height: '90px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-around',
  borderRadius: '10px',
  boxShadow: `1px 3px 4px 0px ${selected ? hexToRGBA(borderColor, 0.2) : 'rgba(0, 0, 0, 0.10)'}`,
  background: '#FFF',
  marginRight: '12px',
  '&:last-child': {
    marginRight: '0px'
  }
}));

const PaymentOptionImage = styled('img')`
  height: 30px;
  margin-right: auto;
`;

const PaymentOptionText = styled('div')`
  color: #10253E;
  font-size: 16px;
  line-height: 1.2;
  font-weight: 600;
`;

const PaymentDetails = styled('div')`
  padding: 20px 0;
`;

const PaymentHiddenWrapper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'show' // Prevent 'show' from being passed to DOM
})(({ show }) => ({
  display: show ? 'block' : 'none',
  textAlign: 'center',
}));

const PaymentContent = styled('div')`
  display: flex;
  align-items: center;
  padding: 30px 0;

  & img {
    width: 134px;
    height: 92px;
  }

  & div {
    margin-left: 20px;
    font-size: 18px;
    text-align: left;
  }
`;

const SurchargeAmountWrapper = styled('div')`
  color: rgba(84, 84, 84, 0.56);
  margin-top: 8px;
  font-size: 13px;
  padding-left: 5px;
  text-align: center;
`;

const INITIAL_PAYMENT_METHOD = 'initial payment method';

export const PaymentForm = () => {
  const navigate = useNavigate();
  const searchString = useLocation().search;
  const { showLoading, hideLoading } = useLoading();

  // Hide HPP PaymentForm for bpay domain.
  if (window.location.hostname === 'bpay.oligo.com.au') {
    // window.location = '/bpay-flow' + window.location.search;
    hideLoading();
    navigate(`/bpay-flow${searchString}`);
  } else if (window.location.hostname === 'aub.oligo.com.au') {
    hideLoading();
    navigate(`/aubridge-flow${searchString}`);
  } else if (window.location.hostname === 'lexusblackburn-sales.oligo.com.au') {
    hideLoading();
    navigate(`/lexus-sales${searchString}`);
  } else if (window.location.hostname === 'lexusblackburn-service.oligo.com.au') {
    hideLoading();
    navigate(`/lexus-service${searchString}`);
  } else if (window.location.hostname === 'knox.oligo.com.au') {
    hideLoading();
    navigate(`/knox-pay${searchString}`);
  } else if (window.location.hostname === 'trinitykew.oligo.com.au') {
    hideLoading();
    navigate(`/trinitykew-pay${searchString}`);
  } else if (window.location.hostname === 'kmp.oligo.com.au') {
    hideLoading();
    navigate(`/kmp-pay${searchString}`);
  }  else if (window.location.hostname === 'payto.oligo.com.au') {
    hideLoading();
    navigate(`/payto-pay${searchString}`);
  } else if (window.location.hostname === 'oxley.oligo.com.au') {
    hideLoading();
    navigate(`/oxley-pay${searchString}`);
  } else if (window.location.hostname === 'codehq.oligo.com.au') {
    hideLoading();
    navigate(`/codehq-pay${searchString}`);
  } else if (window.location.hostname === 'hm.oligo.com.au') {
    hideLoading();
    navigate(`/hm-pay${searchString}`);
  } else if (window.location.hostname === 'payments.oligo.com.au') {
    hideLoading();
    navigate(`/oligo-pay${searchString}`);
  } else if (window.location.hostname === 'sarafi-dandenong.oligo.com.au') {
    hideLoading();
    navigate(`/sarafi-dandenong-pay${searchString}`);
  } else if (window.location.hostname === 'amal-footscray.oligo.com.au') {
    hideLoading();
    navigate(`/amal-footscray-pay${searchString}`);
  } else if (window.location.hostname === 'xero-auth.oligo.com.au') {
    hideLoading();
    navigate(`/xero/user-auth-redirect${searchString}`);
  }

  // https://central.xero.com/s/article/Custom-URL
  // https://developer.xero.com/documentation/guides/how-to-guides/payment-services-integration-with-xero#create-payment-service
  // To deal with the custom payment URL from Xero. https://hostname/?XeroInvoiceNo=[INVOICENUMBER]&XeroCurrency=[CURRENCY]&XeroAmount=[AMOUNTDUE]&XeroShortCode=[SHORTCODE]
  const searchParams = new URLSearchParams(useLocation().search);
  const ipgEnabled = searchParams.get('toggle') === 'ipg';

  const [billAmount, setBillAmount] = useState(searchParams.get('XeroAmount') || '');
  const [billReference, setBillReference] = useState(searchParams.get('XeroInvoiceNo') || '');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [billReferenceError, setBillReferenceError] = useState(false);
  const tillPaymentInlineFormRef = useRef(null);
  const fiservIpgCardRef = useRef(null);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const persistedTransactionId = usePersistedTransactionId();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(INITIAL_PAYMENT_METHOD);

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod('initial payment method');
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handleBillReferenceChange = (value) => {
    setBillReference(value);
    setBillReferenceError(false);
  };

  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'wechatpay':
        return '/api/wechat/create-payment';
      case 'alipay':
        return '/api/alipay/create-payment';
      case 'alipay-dana':
        return '/api/alipay-dana/create-payment';
      default:
        return '';
    }
  }

  const getPaymentMethodName = (selectPayment) => {
    switch (selectPayment) {
      case 'tillPaymentJs':
        return 'Credit Card';
      case 'alipay':
        return 'Alipay';
      default:
        return '';
    }
  }

  const adjustCreatePaymentEndpoint = (endpoint) => {
    if (window.location.hostname === 'www.vidatennis.com.au') return `https://vida.oligo.com.au${endpoint}`;
    return endpoint;
  }

  const createPaymentOrderAndNavigate = async () => {
    if (!billAmount) {
      setBillAmountError(true);
      return;
    }

    if (!billReference) {
      setBillReferenceError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === 'initial payment method') {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
        // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    try {
      if (selectedPaymentMethod === 'tillPaymentJs') {
        // await renderTillPaymentJs();
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult);
      } else if (selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG) {
        await fiservIpgCardRef.current.paymentSubmit(setPaymentResult);
      } else {
        showLoading();

        const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
        const createPaymentEndpointWithAdjustedHost = adjustCreatePaymentEndpoint(createPaymentEndpoint);
        const response = await fetch(`${createPaymentEndpointWithAdjustedHost}?env=${envParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billAmount,
            billReference,
            userEmail,
            persistedTransactionId,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          if (selectedPaymentMethod === 'alipay' || selectedPaymentMethod === 'alipay-dana') {
            // hideLoading('Payment submit successful!', 'success');
            await createPaymentOrderAndNavigateForAlipay(data);
          }
        } else {
          hideLoading('Payment creation failed.', 'error');
        }
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Reference</Label>
              <TextInput
                placeholder={getReferencePlaceholder()}
                value={billReference || ''}
                onChange={handleBillReferenceChange}
                error={billReferenceError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <FormItemContainer>
            <Label>Receipt Email</Label>
            <TextInput
              placeholder="Enter your email if you would like a receipt of payment confirmation"
              value={userEmail || ''}
              onChange={setUserEmail}
            />
          </FormItemContainer>

          <div>
            <Label>Payment Method</Label>
            { !getDefaultPaymentMethod() && <div style={{color: 'rgba(84, 84, 84, 0.56)', marginBottom: '15px', fontSize: '13px'}}>Please select a payment method.</div> }

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange('tillPaymentJs')}
                selected={selectedPaymentMethod === 'tillPaymentJs'}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Card</PaymentOptionText>
              </PaymentOption>
              { !shouldHideAlipayPlus() &&
                <PaymentOption
                  onClick={handlePaymentMethodChange('alipay')}
                  selected={selectedPaymentMethod === 'alipay'}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={AliPayPlusPaymentImage} />
                  <PaymentOptionText>Alipay</PaymentOptionText>
                </PaymentOption>
              }
              { ipgEnabled && (
                <PaymentOption
                  onClick={handlePaymentMethodChange(PAYMENT_METHOD_FISERV_IPG)}
                  selected={selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={CreditCardPaymentImage} />
                  <PaymentOptionText>Credit or Debit Cards</PaymentOptionText>
                </PaymentOption>
              )}
              { shouldShowDevEnvPaymentOptions() && (<>
                <PaymentOption
                  onClick={handlePaymentMethodChange('alipay-dana')}
                  selected={selectedPaymentMethod === 'alipay-dana'}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={AliPayDanaImage} />
                  <PaymentOptionText>Alipay DANA</PaymentOptionText>
                </PaymentOption>
                {/*<PaymentOption*/}
                {/*  onClick={handlePaymentMethodChange('afterpay')}*/}
                {/*  selected={selectedPaymentMethod === 'afterpay'}*/}
                {/*  borderColor={getThemeColor()}*/}
                {/*>*/}
                {/*  <PaymentOptionImage src={AfterPayImage} />*/}
                {/*  <PaymentOptionText>AfterPay</PaymentOptionText>*/}
                {/*</PaymentOption>*/}
                {/*<PaymentOption*/}
                {/*  onClick={handlePaymentMethodChange('zippay')}*/}
                {/*  selected={selectedPaymentMethod === 'zippay'}*/}
                {/*  borderColor={getThemeColor()}*/}
                {/*>*/}
                {/*  <PaymentOptionImage src={ZipPayImage} />*/}
                {/*  <PaymentOptionText>ZipPay</PaymentOptionText>*/}
                {/*</PaymentOption>*/}
              </>)}
            </PaymentOptionList>

            { selectedPaymentMethod !== INITIAL_PAYMENT_METHOD && getDisplaySurchargeRate(selectedPaymentMethod) &&
              <SurchargeAmountWrapper>{getPaymentMethodName(selectedPaymentMethod)} Surcharge of {getDisplaySurchargeRate(selectedPaymentMethod)}</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'tillPaymentJs'}>
                <TillPaymentInlineForm ref={tillPaymentInlineFormRef}
                                       billAmount={billAmount}
                                       billReference={billReference}
                                       userEmail={userEmail}
                                       initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}
                                       creditCardImage={getCreditCardPaymentImage()}
                />
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'alipay'}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_FISERV_IPG}>
                <FiservIpgCardPayment
                  ref={fiservIpgCardRef}
                  billAmount={billAmount}
                  billReference={billReference}
                  userEmail={userEmail}
                  creditCardImage={getCreditCardPaymentImage()}
                />
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'alipay-dana'}>
                <img style={{height: '22px', marginLeft: 'auto'}} src={AliPayDanaImage}/>
                <div style={{textAlign: 'center'}}>
                  AliPay DANA
                </div>
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'afterpay'}>
                <img style={{marginBottom: '20px', height: '100px'}} src={ExternalPayImage}/>
                <div style={{textAlign: 'center'}}>
                  Click to be securely sent to Afterpay where you will finalise your purchase.
                </div>
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'zippay'}>
                <img style={{marginBottom: '20px', height: '100px'}} src={ExternalPayImage}/>
                <div style={{textAlign: 'center'}}>
                  Zip money - Buy Now, Pay Later. Upto 12 Months Interest Free.
                </div>
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
